import api from "@/assets/js/utils/api.js"; // 你自己的 API 呼叫模組
import VueApexCharts from "vue3-apexcharts";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { library } from "@fortawesome/fontawesome-svg-core";
import {
  faShoppingCart,
  faDollarSign,
  faUsers,
  faEye,
  faArrowUp,
  faArrowDown,
  faCog, // ✅ 新增
} from "@fortawesome/free-solid-svg-icons";

library.add(
  faShoppingCart,
  faDollarSign,
  faUsers,
  faEye,
  faArrowUp,
  faArrowDown,
  faCog, // ✅ 新增
);

export default {
  name: "DashboardView",

  components: {
    apexchart: VueApexCharts,
    FontAwesomeIcon,
  },

  data() {
    return {
      selectedPeriod: "week",

      // ✅ 新增：聯絡資訊 Modal 狀態與模擬資料
      isContactModalOpen: false,
      rawContactInfo: {
        address: "台北市信義區忠孝東路五段 100 號",
        phone: "02-2720-8889",
      },
      contactData: {
        address: "",
        phone: "",
      },

      stats: [], // 訂單 & 營業額
      revenueData: [], // 營業額趨勢
      pieSeries: [], // 熱門分類 %
      pieOptions: {
        // ★★★ 預先帶入 chart 設定
        chart: { type: "donut" },
        labels: [],
        colors: ["#4A90E2", "#7ED321", "#F5A623", "#F66E6E"],
        legend: { position: "bottom" },
        dataLabels: {
          enabled: true,
          formatter: (val) => `${val.toFixed(1)}%`,
        },
      },
      recentOrders: [], // 最新訂單
      inventoryAlerts: [], // 庫存警示

      barSeries: [], // 柱狀圖的資料
      barOptions: {
        chart: { type: "bar", toolbar: { show: false } }, // 隱藏工具列
        plotOptions: {
          bar: {
            columnWidth: "45%", // 柱子寬度
            borderRadius: 6, // 圓角
            distributed: true, // 每根柱子不同顏色
          },
        },
        dataLabels: { enabled: false }, // 不要在柱子上顯示數字
        xaxis: {
          categories: [], // 日期（loadDashboard 裡會塞資料）
        },
        colors: [
          "#4A90E2",
          "#7ED321",
          "#F5A623",
          "#F66E6E",
          "#50E3C2",
          "#B8E986",
          "#F8E71C",
          "#BD10E0",
        ],
      },
    };
  },

  mounted() {
    this.loadDashboard();
    // ✅ 從後端加載聯絡資訊
    this.loadContactInfo();
  },

  methods: {
    // ✅ 新增：從後端加載聯絡資訊
    async loadContactInfo() {
      try {
        const res = await api.get("/dashboard/contact");
        if (res.data.success && res.data.data) {
          const contactData = res.data.data;
          this.rawContactInfo = {
            address: contactData.Address || "",
            phone: contactData.Phone || "",
          };
          this.contactData = { ...this.rawContactInfo };
        }
      } catch (err) {
        console.error("加載聯絡資訊失敗:", err);
        // 如果加載失敗，保持原有資料
      }
    },

    // ✅ 新增：自定義 Modal 操作方法
    openContactModal() {
      this.contactData = { ...this.rawContactInfo };
      this.isContactModalOpen = true;
    },
    closeContactModal() {
      this.isContactModalOpen = false;
    },
    async handleSaveContact() {
      try {
        // 調用後端 API 更新聯絡資訊
        const res = await api.post("/dashboard/contact", {
          address: this.contactData.address,
          phone: this.contactData.phone,
        });

        if (res.data.success) {
          // 更新本地資料
          this.rawContactInfo = { ...this.contactData };
          this.isContactModalOpen = false;

          // 成功提示
          const Swal = (await import("sweetalert2")).default;
          Swal.fire({
            icon: "success",
            title: "儲存成功",
            timer: 1000,
            showConfirmButton: false,
          });
        } else {
          // API 返回失敗
          const Swal = (await import("sweetalert2")).default;
          Swal.fire({
            icon: "error",
            title: "儲存失敗",
            text: res.data.message || "無法更新聯絡資訊",
          });
        }
      } catch (err) {
        console.error("儲存聯絡資訊失敗:", err);
        const Swal = (await import("sweetalert2")).default;
        Swal.fire({
          icon: "error",
          title: "儲存失敗",
          text: "網路錯誤，請稍後重試",
        });
      }
    },

    async loadDashboard() {
      try {
        const res = await api.get(`/dashboard?period=${this.selectedPeriod}`);

        const d = res.data.data;

        // ---- 統計卡片 ----
        this.stats = [
          {
            label: "營業額",
            value: "NT$" + this.formatNumber(d.Revenue),
            trend:
              d.RevenueGrowth > 0
                ? "up"
                : d.RevenueGrowth < 0
                ? "down"
                : "none",
            change: (d.RevenueGrowth > 0 ? "+" : "") + d.RevenueGrowth + "%",
            color: "#7ED321",
            icon: ["fas", "dollar-sign"],
          },
          {
            label: "已付款金額",
            value: "NT$" + this.formatNumber(d.PaidAmount),
            trend: "none",
            color: "#27AE60",
            icon: ["fas", "dollar-sign"],
            showArrow: false,
          },
          {
            label: "未付款金額",
            value: "NT$" + this.formatNumber(d.UnPaidAmount),
            trend: "none",
            color: "#E74C3C",
            icon: ["fas", "dollar-sign"],
            showArrow: false,
          },
          {
            label: "訂單數",
            value: d.OrderCount,
            trend:
              d.OrderGrowth > 0 ? "up" : d.OrderGrowth < 0 ? "down" : "none",
            change: (d.OrderGrowth > 0 ? "+" : "") + d.OrderGrowth + "%",
            color: "#4A90E2",
            icon: ["fas", "shopping-cart"],
          },

          {
            label: "已出貨訂單",
            value: d.ShippedOrderCount,
            trend: "none",
            color: "#9B51E0",
            icon: ["fas", "shopping-cart"],
            showArrow: false,
          },
          {
            label: "未出貨訂單",
            value: d.UnShippedOrderCount,
            trend: "none",
            color: "#F5A623",
            icon: ["fas", "shopping-cart"],
            showArrow: false,
          },
        ];

        // ---- ⭐ 新增：ApexCharts 柱狀圖資料 ----
        this.barSeries = [
          {
            name: "營業額",
            data: d.RevenueTrend.map((x) => x.Amount),
          },
        ];

        this.barOptions = {
          ...this.barOptions, // 保留原設定
          xaxis: {
            categories: d.RevenueTrend.map((x) => x.Date),
          },
        };

        // ---- 熱門分類 ----
        this.pieSeries = d.HotCategories.map((x) => x.PercentValue);
        this.pieOptions = {
          labels: d.HotCategories.map((x) => x.CategoryName),
        };

        // ---- 最新訂單 ----
        this.recentOrders = d.RecentOrders.map((o) => ({
          orderNumber: o.OrderNo,
          customerName: o.CustomerName,
          amount: o.TotalAmount,
          status: o.OrderStatus,
          date: o.CreatedDate.substring(0, 10),
          statusClass: this.mapStatusClass(o.OrderStatus),
        }));

        // ---- 庫存警示 ----
        this.inventoryAlerts = d.InventoryAlerts.map((p) => ({
          name: p.ProductName,
          currentStock: p.Stock,
          alertStatus: this.detectAlertStatus(p.Stock),
          alertClass: this.detectAlertClass(p.Stock),
        }));
      } catch (err) {
        console.log(err);
      }
    },

    // ===== 狀態映射 =====
    detectAlertStatus(stock) {
      if (stock < 5) return "緊急";
      if (stock < 10) return "警告";
      return "正常";
    },

    detectAlertClass(stock) {
      if (stock < 5) return "urgent";
      if (stock < 10) return "warning";
      return "normal";
    },

    mapStatusClass(status) {
      const classes = {
        待處理: "status-pending", // 黃
        處理中: "status-processing", // 藍
        已出貨: "status-shipped", // 紫
        已完成: "status-completed", // 綠
        已取消: "status-cancelled", // 紅
        申請退貨: "status-return", // 橘色
      };
      return classes[status] || "status-default";
    },

    formatCurrency(num) {
      return "$" + this.formatNumber(num); // 使用你原本的 formatNumber
    },

    // 數字格式
    formatNumber(num) {
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },
  },

  watch: {
    selectedPeriod() {
      this.loadDashboard(); // ★★★ 每次切換時間區間就重新抓資料
    },
  },
};
